import React, { useState, useRef } from 'react';
import Icon from '@mdi/react';
import { mdiMagnify, mdiAccountMultiplePlusOutline, mdiAccountCircle, mdiAccountOutline, mdiPaw, mdiAccountCircleOutline } from '@mdi/js';
import { toast } from 'react-toastify';
import { classApi } from '../../api/classes';
import BaseModal from '../BaseModal';
import './EnrolStudentModal.css';

/**
 * EnrolStudentModal - Search for and enrol an existing registered student
 * Props:
 *   classId  - the class to enrol into
 *   termId   - the term the class belongs to
 *   className - display name for the header
 *   onClose
 *   onEnrolled - called after successful enrolment
 */
const RoleToggle = ({ role, onChange }) => (
    <div className="role-toggle">
        <button
            type="button"
            className={`role-toggle-btn${role === 'leader' ? ' active-leader' : ''}`}
            onClick={() => onChange(role === 'leader' ? null : 'leader')}>
            Leader
        </button>
        <button
            type="button"
            className={`role-toggle-btn${role === null ? ' active-unspecified' : ''}`}
            onClick={() => onChange(null)}>
            Not specified
        </button>
        <button
            type="button"
            className={`role-toggle-btn${role === 'follower' ? ' active-follower' : ''}`}
            onClick={() => onChange(role === 'follower' ? null : 'follower')}>
            Follower
        </button>
    </div>
);

const EnrolStudentModal = ({ classId, termId, shortCourseId, className, onClose, onEnrolled }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [enrollingId, setEnrollingId] = useState(null);
    const [enrolledIds, setEnrolledIds] = useState(new Set());
    const [role, setRole] = useState(null);
    const debounceRef = useRef(null);

    const handleQueryChange = (e) => {
        const q = e.target.value;
        setQuery(q);
        clearTimeout(debounceRef.current);

        if (q.length < 2) {
            setResults([]);
            return;
        }

        debounceRef.current = setTimeout(async () => {
            setIsSearching(true);
            try {
                const res = await classApi.searchStudents(q);
                if (res.ok) setResults(res.data.students || []);
            } catch {
                // fail silently
            } finally {
                setIsSearching(false);
            }
        }, 300);
    };

    const handleEnrol = async (student) => {
        setEnrollingId(student.id);
        try {
            const data = shortCourseId
                ? {
                    shortCourseId,
                    studentId: student.source === 'account' ? student.id : null,
                    studentProfileId: student.source === 'student-profile' ? student.id : null,
                    status: 'confirmed',
                    role,
                }
                : {
                    classId,
                    termId,
                    studentId: student.source === 'account' ? student.id : null,
                    studentProfileId: student.source === 'student-profile' ? student.id : null,
                    status: 'confirmed',
                    role,
                };
            const res = await classApi.createEnrolment(data);
            if (res.ok) {
                setEnrolledIds(prev => new Set([...prev, student.id]));
                toast.success(`${student.firstName} ${student.lastName} enrolled`);
                onEnrolled();
            } else {
                toast.error(res.data?.message || 'Failed to enrol student');
            }
        } catch {
            toast.error('Failed to enrol student');
        } finally {
            setEnrollingId(null);
        }
    };

    return (
        <BaseModal
            isOpen
            onClose={onClose}
            title="Enrol Student"
            icon={mdiAccountMultiplePlusOutline}
            maxWidth={500}
            footer={<button className="base-modal-cancel-btn" onClick={onClose}>Close</button>}
        >
            {/* Sticky search — stays fixed while results scroll */}
            <div className="enrol-modal-search-sticky">
                {className && <span className="enrol-modal-class-pill">{className}</span>}
                <p className="enrol-modal-role-label">Select role for this class (optional)</p>
                <p className="enrol-modal-role-hint">Leader/follower is set per enrolment — the same student can be a leader in one class and a follower in another.</p>
                <RoleToggle role={role} onChange={setRole} />
                <div className="enrol-modal-search-row">
                    <Icon path={mdiMagnify} size={0.9} color="#888" />
                    <input
                        type="text"
                        className="enrol-modal-search-input"
                        placeholder="Search by name or email..."
                        value={query}
                        onChange={handleQueryChange}
                        autoFocus
                    />
                    {isSearching && <span className="enrol-modal-searching">Searching...</span>}
                </div>
            </div>

            {query.length < 2 && (
                <p className="enrol-modal-hint">Type at least 2 characters to search</p>
            )}

            {query.length >= 2 && !isSearching && results.length === 0 && (
                <p className="enrol-modal-no-results">No students found for "{query}"</p>
            )}

            {results.length > 0 && (
                <div className="enrol-modal-results">
                    {results.map(student => {
                        const isEnrolled = enrolledIds.has(student.id);
                        const isLoading = enrollingId === student.id;
                        return (
                            <div key={student.id} className={`enrol-modal-result-row${isEnrolled ? ' enrolled' : ''}`}>
                                <div className="enrol-modal-result-avatar">
                                    <Icon
                                        path={student.source === 'account' ? mdiAccountCircle : mdiAccountOutline}
                                        size={1.1}
                                        color={student.source === 'account' ? '#451f78' : '#888'}
                                    />
                                </div>
                                <div className="enrol-modal-result-info">
                                    <span className="enrol-modal-result-name">
                                        {student.firstName} {student.lastName}
                                    </span>
                                    {student.email && (
                                        <span className="enrol-modal-result-email">{student.email}</span>
                                    )}
                                </div>
                                {student.source === 'account' ? (
                                    <div className="class-students-account-banner class-students-account-banner--platform">
                                        <Icon path={mdiPaw} size={0.55} color="var(--dl-color-purple-default)" />
                                        <span>Dance Localé member</span>
                                    </div>
                                ) : (
                                    <div className="class-students-account-banner class-students-account-banner--school-only">
                                        <Icon path={mdiAccountCircleOutline} size={0.55} />
                                        <span>School account only</span>
                                    </div>
                                )}
                                {isEnrolled ? (
                                    <span className="enrol-modal-enrolled-label">Enrolled</span>
                                ) : (
                                    <button
                                        className="enrol-modal-enrol-btn"
                                        onClick={() => handleEnrol(student)}
                                        disabled={isLoading || !!enrollingId}>
                                        {isLoading ? '...' : 'Enrol'}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </BaseModal>
    );
};

export default EnrolStudentModal;
